import { utilities } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import moment from 'moment';

import {
  billingInvoiceService,
  callLogsService,
  rtbLogsService,
  cacheService
} from '@/angularBridgeCode/ringbaPortal.servicesModule';

import {
  ADD_NEW_EXPORT_JOB,
  UPDATE_EXPORT_JOB_STATUS,
  DISMISS_AND_CLEAR_JOB,
  UPDATE_ACTIVE_TOAST_ID,
  CACHE_JOB_BEEN_CHECKED
} from './mutationTypes';

const dateFormat = 'MMM D, YYYY';
const jobTypeExportCC = 'ccInvoice';
const jobTypeRtbLogs = 'RtbLogs';
const jobTypeCallLogsV2 = 'CallLogsV2';
const jobTypeCallLogs = 'CallLogs';
const jobTypeMonthyInvoice = 'JobTypeMonthyInvoice';
const downloadInvoiceTitle = 'Download Invoice';

async function exportCCInvoiceStatusCheck(commit, accountId, jobId) {
  try {
    const jobStatusResp = await billingInvoiceService.getStatusExportCreditCardInvoice(accountId, jobId);
    commit(UPDATE_EXPORT_JOB_STATUS, _parseFileExportBased(jobStatusResp.data));
  } catch (e) {
    // Intentional empty try/catch on job status update. It should keep trying to get the job status from the api
  }
}
async function exportRtbLogsStatusCheck(commit, accountId, jobId) {
  try {
    const jobStatusResp = await rtbLogsService.getExportStatus(accountId, jobId);
    jobStatusResp.id = jobId;
    commit(UPDATE_EXPORT_JOB_STATUS, jobStatusResp);
  } catch (e) {
    //
  }
}

async function exportCallLogsV2StatusCheck(commit, accountId, jobId) {
  try {
    const jobStatusResp = await callLogsService.getExportStatusV2(accountId, jobId);
    jobStatusResp.id = jobId;
    commit(UPDATE_EXPORT_JOB_STATUS, jobStatusResp);
  } catch (e) {
    //
  }
}

async function exportCallLogsStatusCheck(commit, accountId, jobId) {
  try {
    const jobStatusResp = await callLogsService.getExportStatus(accountId, jobId);
    jobStatusResp.id = jobId;
    commit(UPDATE_EXPORT_JOB_STATUS, jobStatusResp);
  } catch (e) {
    //
  }
}

async function exportMonthBillingInvoiceStatusCheck(commit, accountId, jobId) {
  try {
    const jobStatusResp = await billingInvoiceService.getStatusExportMonthlyBillingInvoice(accountId, jobId);
    commit(UPDATE_EXPORT_JOB_STATUS, _parseFileExportBased(jobStatusResp.data));
  } catch (e) {
    //
  }
}

function getJobStatusCheckHandler(commit, accountId, jobId, jobType) {
  switch (jobType) {

  case jobTypeExportCC:
      return function () { exportCCInvoiceStatusCheck(commit, accountId, jobId); };
    
  case jobTypeRtbLogs:
    return function () { exportRtbLogsStatusCheck(commit, accountId, jobId); };
    
  case jobTypeCallLogsV2:
    return function () { exportCallLogsV2StatusCheck(commit, accountId, jobId); };
    
  case jobTypeCallLogs:
    return function () { exportCallLogsStatusCheck(commit, accountId, jobId); };

  case jobTypeMonthyInvoice:
    return function () { exportMonthBillingInvoiceStatusCheck(commit, accountId, jobId); };

  default:
    return null;
  }
}

function getPendingJobsCacheKey(state, accountId) {
  return state.pendingJobsExportCacheKey + accountId; // TO DO scope if we need a different prefix for the new export
}

function popExportToaster(commit) {
  if (document.querySelector('.toast-loader')) {
    utilities.emitToasterUpdateEvent();
    return; 
  }

  const toastId = JSON.stringify(new Date().getTime());
  utilities.showToasterWithOptions({
    toasterId: 'miscellaneous-toaster',
    toastId: toastId,
    type: 'loader',
    body: 'vue-export-file-loader',
    bodyOutputType: 'directive',
    timeout: 0,
    tapToDismiss: false,
    directiveData: {
      toastId: toastId
    }
  });

  commit(UPDATE_ACTIVE_TOAST_ID, toastId);
}

function _parseFileExportBased(jobStatus) {
  return {
    id: jobStatus.id,
    url: jobStatus.fileURL,
    status: jobStatus.state,
    progressPercent: jobStatus.progressPercent,
    progressStage: jobStatus.progressStage
  };
}

const actions = {
  
  checkPendingJobs({ rootState, state, commit }) {
    if (state.pendingExportJobsChecked) {
      return;
    }

    const pendingJobs = cacheService.get(getPendingJobsCacheKey(state, rootState.currentAccountId));
    commit(CACHE_JOB_BEEN_CHECKED);

    if (!pendingJobs || _.isEmpty(pendingJobs)) {
      return;
    }
    
    const handlers = [];
    _.forEach(pendingJobs, (job) => {

      const handler = getJobStatusCheckHandler(commit, rootState.currentAccountId, job.id, job.type);
      
      if (handler) {
        handlers.push(handler);

        commit(ADD_NEW_EXPORT_JOB, {
          accountId: rootState.currentAccountId,
          jobId: job.id,
          exportName: job.name,
          downloadTitle: job.downloadTitle,
          isRestoringPendingJob: true,
          jobStatusHandler: handler
        });
      }
    });

    handlers.forEach(handler => {
      handler();
    });   
    
    popExportToaster(commit);
  },

  async exportCCInvoice({ rootState, commit }, { transaction }) {
    const exportName = `Credit Card Statement (${transaction.cC_Last_4} - ${moment(transaction.dtStamp).format(dateFormat)})`;
      
    await billingInvoiceService.exportCreditCardTransactionInvoice(rootState.currentAccountId, transaction.refId).then((resp) => {

      const jobId = resp.data.jobId;
      const jobType = jobTypeExportCC;
      const jobStatusHandler = getJobStatusCheckHandler(commit, rootState.currentAccountId, jobId, jobType);

      commit(ADD_NEW_EXPORT_JOB, { accountId: rootState.currentAccountId, jobId, jobType, exportName, downloadInvoiceTitle, jobStatusHandler });
      popExportToaster(commit);
    });
  },

  async exportRtbLogs({ rootState, commit }, { apiRequestData, exportName }) {
    try {
      const resp = await rtbLogsService.exportRtbLogs(rootState.currentAccountId, apiRequestData, exportName, true);
      const jobId = resp.data.id;
      const jobType = jobTypeRtbLogs;
      const jobStatusHandler = getJobStatusCheckHandler(commit, rootState.currentAccountId, jobId, jobType);
      
      commit(ADD_NEW_EXPORT_JOB, { accountId: rootState.currentAccountId, jobId, jobType, exportName, jobStatusHandler });
      popExportToaster(commit);
    } catch (e) {
      utilities.errorHandler(e);
    }
  },

  async exportCallLogsV2({ rootState, commit }, { apiRequestData, exportName }) {

    try {
      const resp = await callLogsService.exportCallLogsV2(rootState.currentAccountId, apiRequestData, exportName, true);
      const jobId = resp.data.id;
      const jobType = jobTypeCallLogsV2;
      const jobStatusHandler = getJobStatusCheckHandler(commit, rootState.currentAccountId, jobId, jobType);
      
      commit(ADD_NEW_EXPORT_JOB, { accountId: rootState.currentAccountId, jobId, jobType, exportName, jobStatusHandler });
      popExportToaster(commit);
    } catch (e) {
      utilities.errorHandler(e);
    }
  },

  async exportCallLogs({ rootState, commit }, { reportSettings, exportName, viewAsBuyerId, viewAsPublisherId }) {
    await callLogsService.exportData(rootState.currentAccountId, reportSettings, exportName, viewAsBuyerId, viewAsPublisherId).then((resp) => {

      const jobId = resp.data.id;
      const jobType = jobTypeCallLogs;
      const jobStatusHandler = getJobStatusCheckHandler(commit, rootState.currentAccountId, jobId, jobType);
      
      commit(ADD_NEW_EXPORT_JOB, { accountId: rootState.currentAccountId, jobId, jobType, exportName, jobStatusHandler });
      popExportToaster(commit);
    });
  },

  async exportMonthBillingInvoice({ rootState, commit }, { year, month, exportName }) {
    try {
      var resp = await billingInvoiceService.exportMonthlyBillingInvoice(rootState.currentAccountId, year, month);
      const jobId = resp.data.job.id;
      const jobType = jobTypeMonthyInvoice;
      const jobStatusHandler = getJobStatusCheckHandler(commit, rootState.currentAccountId, jobId, jobType);
      
      commit(DISMISS_AND_CLEAR_JOB, { accountId: rootState.currentAccountId, jobId });
      commit(ADD_NEW_EXPORT_JOB, { accountId: rootState.currentAccountId, jobId, jobType, exportName, downloadInvoiceTitle, jobStatusHandler });
      popExportToaster(commit);
    }
    catch (e) {
      utilities.showToaster(
        'error',
        e.data.message
      );
    }
  },

  async dismissAndClearJob({ rootState, commit }, jobId) {
    commit(DISMISS_AND_CLEAR_JOB, { jobId, accountId: rootState.currentAccountId });
  },
};

export default actions;